import {
  BaseballAtBatCountAttribute,
  BaseballHitAttribute,
  BaseballIncidentId,
  BaseballOutAttribute,
  BaseballStrikeoutAttribute,
  BaseballUnderReviewAttribute,
  SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'

import {
  HistoryIncidentsConfig,
  SubParticipantSettings,
} from '../../configuration'
import { sortPitcherIncidentOptions } from './pitcherOrder'

function createBatterSubparticipantSettings(
  name: string,
): SubParticipantSettings {
  return {
    name,
    required: true,
    filterSubParticipantsOptions: (options) =>
      options.filter(
        (option) =>
          option.metadata.position === SubParticipantPosition.Batter ||
          (option.metadata.position === SubParticipantPosition.Pitcher &&
            option.metadata.hasSecondaryPosition),
      ),
  }
}

function createPitcherSubparticipantSettings(
  name: string,
): SubParticipantSettings {
  return {
    name,
    required: true,
    filterSubParticipantsOptions: (options) =>
      sortPitcherIncidentOptions(
        options.filter(
          (option) =>
            option.metadata.position === SubParticipantPosition.Pitcher ||
            (option.metadata.position === SubParticipantPosition.Batter &&
              option.metadata.hasSecondaryPosition),
        ),
      ),
  }
}

const batterSettings = createBatterSubparticipantSettings('batter')
const pitcherSettings = createPitcherSubparticipantSettings('pitched by')

export const historyIncidentsConfig: HistoryIncidentsConfig<BaseballIncidentId> =
  {
    [BaseballIncidentId.UnderReview]: {
      singleAttributes: true,
      attributeIds: [
        BaseballUnderReviewAttribute.CallStands,
        BaseballUnderReviewAttribute.CallOverturned,
      ],
    },
    [BaseballIncidentId.ThrowPitcher]: {
      participant: pitcherSettings,
    },
    [BaseballIncidentId.PlateAppearance]: {
      participant: batterSettings,
      singleAttributes: true,
      attributeIds: [BaseballAtBatCountAttribute.AtBatDelete],
    },
    [BaseballIncidentId.PickOffAttempt]: {
      participant: pitcherSettings,
    },
    [BaseballIncidentId.Ball]: {
      participant: {
        ...batterSettings,
        shouldLoadOppositeSubParticipants: true,
      },
    },
    [BaseballIncidentId.Strike]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.SwingAndMiss]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.Foul]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.Strikeout]: {
      participant: batterSettings,
      singleAttributes: true,
      attributeIds: [BaseballStrikeoutAttribute.UncaughtThirdStrike],
    },
    [BaseballIncidentId.Out]: {
      participant: batterSettings,
      assistant: {
        ...pitcherSettings,
        shouldLoadOppositeSubParticipants: true,
      },
      singleAttributes: true,
      attributeIds: [
        BaseballOutAttribute.SacrificeBunt,
        BaseballOutAttribute.SacrificeFly,
      ],
    },
    [BaseballIncidentId.Hit]: {
      participant: batterSettings,
      attributeIds: [
        BaseballHitAttribute.Single,
        BaseballHitAttribute.Double,
        BaseballHitAttribute.Triple,
        BaseballHitAttribute.BackToBackHit,
      ],
    },
    [BaseballIncidentId.HomeRun]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.Run]: {
      participant: createBatterSubparticipantSettings('earned by'),
      assistant: createBatterSubparticipantSettings('RBI by'),
      secondary_assistant: createBatterSubparticipantSettings('current batter'),
    },
    [BaseballIncidentId.Walk]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.StolenBase]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.CaughtStealing]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.WildPitch]: {
      participant: pitcherSettings,
    },
    [BaseballIncidentId.HitByPitch]: {
      participant: batterSettings,
    },

    [BaseballIncidentId.Error]: {
      participant: {
        name: 'player',
        required: true,
      },
    },
    [BaseballIncidentId.PitcherChange]: {
      participant: pitcherSettings,
    },
    [BaseballIncidentId.BatterChange]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.FirstBase]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.SecondBase]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.ThirdBase]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.FirstBaseOff]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.SecondBaseOff]: {
      participant: batterSettings,
    },
    [BaseballIncidentId.ThirdBaseOff]: {
      participant: batterSettings,
    },
  }
